import { useEffect, useState } from 'react';
import { Envelope, Plus, PencilSimple, Trash, Star, StarHalf, Check, X, Warning, Cloud, Lock, Plug } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ kind, children }) {
  const styles = kind === 'success'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : kind === 'error'
    ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-blue-50 border-blue-200 text-blue-800';
  return <div className={`px-3 py-2 text-xs rounded-sm border flex items-center gap-2 ${styles}`}>{children}</div>;
}

const EMPTY = {
  email: '', display_name: '', from_name: '', signature_html: '',
  type: 'graph',
  imap: { host: '', port: 993, ssl: true, username: '', password: '', inbox_folder: 'INBOX' },
  smtp: { host: '', port: 465, ssl_mode: 'ssl', username: '', password: '' },
};

export default function MailboxesPage() {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // mailbox object or null
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const fireToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/marketing/mailboxes`, { credentials: 'include' });
      if (r.ok) setMailboxes((await r.json()).mailboxes || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setTestResult(null);
    setShowForm(true);
  };

  const startEdit = (m) => {
    setEditing(m);
    setForm({
      email: m.email,
      display_name: m.display_name || '',
      from_name: m.from_name || '',
      signature_html: m.signature_html || '',
      type: m.type || 'graph',
      imap: {
        host: m.imap?.host || '', port: m.imap?.port || 993,
        ssl: m.imap?.ssl !== false,
        username: m.imap?.username || '',
        password: '',  // never reveal — only update when user types a new one
        inbox_folder: m.imap?.inbox_folder || 'INBOX',
      },
      smtp: {
        host: m.smtp?.host || '', port: m.smtp?.port || 465,
        ssl_mode: m.smtp?.ssl_mode || 'ssl',
        username: m.smtp?.username || '',
        password: '',
      },
    });
    setTestResult(null);
    setShowForm(true);
  };

  const cancel = () => { setShowForm(false); setEditing(null); setForm(EMPTY); setTestResult(null); };

  const buildPayload = () => {
    // Only include IMAP/SMTP block when type=imap_smtp.
    const base = {
      email: form.email, display_name: form.display_name,
      from_name: form.from_name, signature_html: form.signature_html,
      type: form.type,
    };
    if (form.type === 'imap_smtp') {
      // Don't ship empty `password` strings on edits — backend interprets them as "no change".
      const imap = { ...form.imap };
      if (!imap.password) delete imap.password;
      const smtp = { ...form.smtp };
      if (!smtp.password) delete smtp.password;
      base.imap = imap;
      base.smtp = smtp;
    }
    return base;
  };

  const testConnection = async () => {
    if (!editing) {
      setTestResult({ ok: false, error: 'Sla de mailbox eerst op om de verbinding te testen.' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/api/marketing/mailboxes/${editing.mailbox_id}/test`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      setTestResult(d);
    } catch (e) {
      setTestResult({ ok: false, error: String(e) });
    }
    setTesting(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      const url = editing
        ? `${API}/api/marketing/mailboxes/${editing.mailbox_id}`
        : `${API}/api/marketing/mailboxes`;
      const r = await fetch(url, {
        method: editing ? 'PUT' : 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        fireToast('success', editing ? 'Mailbox bijgewerkt' : 'Mailbox toegevoegd');
        cancel();
        await load();
      } else {
        fireToast('error', data.detail || 'Opslaan mislukt');
      }
    } catch {
      fireToast('error', 'Verbindingsfout');
    }
    setBusy(false);
  };

  const setDefault = async (mailboxId) => {
    try {
      const r = await fetch(`${API}/api/marketing/mailboxes/${mailboxId}/set-default`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) { fireToast('success', 'Standaard ingesteld'); await load(); }
      else fireToast('error', 'Mislukt');
    } catch {
      fireToast('error', 'Verbindingsfout');
    }
  };

  const remove = async (mailboxId) => {
    try {
      const r = await fetch(`${API}/api/marketing/mailboxes/${mailboxId}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) { fireToast('success', 'Mailbox verwijderd'); await load(); }
      else fireToast('error', data.detail || 'Verwijderen mislukt');
    } catch {
      fireToast('error', 'Verbindingsfout');
    }
    setConfirmDelete(null);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="mailboxes-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A]">Verzendadressen</h1>
          <p className="text-sm text-zinc-500 mt-1">Beheer afzender e-mailadressen en hun handtekening</p>
        </div>
        {!showForm && (
          <button
            type="button"
            data-testid="add-mailbox-btn"
            onClick={startCreate}
            className="px-4 py-2 text-xs bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] transition-colors flex items-center gap-1"
          >
            <Plus size={12} /> Nieuw e-mailadres
          </button>
        )}
      </div>

      {toast && <div className="mb-4" data-testid="mailbox-toast"><Toast kind={toast.kind}>{toast.msg}</Toast></div>}

      {/* Form */}
      {showForm && (
        <div className="mb-6 bg-white border border-[#0055FF] rounded-sm p-5" data-testid="mailbox-form">
          <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-4">
            {editing ? 'Mailbox bewerken' : 'Nieuwe mailbox'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">E-mailadres *</label>
              <input
                data-testid="form-email"
                type="email" required
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                disabled={!!editing}
                placeholder="info@horseappcompare.com"
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A] disabled:bg-zinc-50 disabled:text-zinc-500"
              />
              {editing && <p className="text-[11px] text-zinc-400 mt-1">E-mailadres kan niet gewijzigd worden</p>}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Type *</label>
              <select
                data-testid="form-type"
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                disabled={!!editing}
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A] disabled:bg-zinc-50 disabled:text-zinc-500"
              >
                <option value="graph">Microsoft 365 (Graph API)</option>
                <option value="imap_smtp">IMAP/SMTP (TransIP, Gmail, anders)</option>
              </select>
              {editing && <p className="text-[11px] text-zinc-400 mt-1">Type kan niet gewijzigd worden</p>}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Weergavenaam (intern)</label>
              <input
                data-testid="form-display-name"
                value={form.display_name}
                onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                placeholder="Bijv: HorseAppCompare info"
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Afzender naam (in e-mail)</label>
              <input
                data-testid="form-from-name"
                value={form.from_name}
                onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))}
                placeholder="Bijv: Martijn Wirken"
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
              />
            </div>
          </div>

          {form.type === 'imap_smtp' && (
            <ImapSmtpFields
              form={form}
              setForm={setForm}
              editing={!!editing}
              onTest={testConnection}
              testing={testing}
              testResult={testResult}
            />
          )}

          <div className="mb-4">
            <label className="text-xs text-zinc-500 mb-1 block">
              Handtekening (HTML)
              <span className="text-[11px] text-zinc-400 ml-2">— wordt automatisch onderaan elke campagne-mail toegevoegd</span>
            </label>
            <textarea
              data-testid="form-signature-html"
              value={form.signature_html}
              onChange={e => setForm(f => ({ ...f, signature_html: e.target.value }))}
              rows={10}
              placeholder="Laat leeg om standaard PoolFIX-handtekening te gebruiken"
              className="w-full px-3 py-2 text-xs font-mono border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
            />
            {form.signature_html && (
              <div className="mt-3 p-3 border border-dashed border-zinc-300 bg-zinc-50 rounded-sm">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Voorbeeld</div>
                <div data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: form.signature_html }} />
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              data-testid="form-cancel-btn"
              onClick={cancel}
              className="px-4 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors"
            >
              Annuleer
            </button>
            <button
              type="button"
              data-testid="form-save-btn"
              onClick={save}
              disabled={busy || !form.email}
              className="px-4 py-2 text-xs bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-40 transition-colors flex items-center gap-1"
            >
              <Check size={12} /> {busy ? 'Bezig...' : (editing ? 'Bijwerken' : 'Aanmaken')}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-zinc-200 rounded-sm" data-testid="mailboxes-list">
        {loading ? (
          <div className="p-8 text-center text-xs text-zinc-500">Laden...</div>
        ) : mailboxes.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">
            Nog geen mailboxes. Klik op <strong>Nieuw e-mailadres</strong> om te starten.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {mailboxes.map(m => (
              <div
                key={m.mailbox_id}
                data-testid={`mailbox-row-${m.mailbox_id}`}
                className="p-4 flex items-start justify-between gap-4 hover:bg-zinc-50/50 transition-colors"
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-sm bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Envelope size={18} className="text-zinc-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-[#0F172A]">{m.email}</span>
                      {m.type === 'imap_smtp' ? (
                        <span
                          data-testid={`mailbox-type-${m.mailbox_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium rounded-sm bg-indigo-50 text-indigo-700"
                        >
                          <Plug size={10} weight="bold" /> IMAP/SMTP
                        </span>
                      ) : (
                        <span
                          data-testid={`mailbox-type-${m.mailbox_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium rounded-sm bg-sky-50 text-sky-700"
                        >
                          <Cloud size={10} weight="bold" /> Microsoft 365
                        </span>
                      )}
                      {m.is_default && (
                        <span
                          data-testid={`mailbox-default-badge-${m.mailbox_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium rounded-sm bg-amber-50 text-amber-800"
                        >
                          <Star size={10} weight="fill" /> Standaard
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                      {m.display_name || '—'}
                      {m.from_name && <span className="text-zinc-400"> · afzender: {m.from_name}</span>}
                    </div>
                    {m.signature_html && (
                      <div className="mt-2 text-[11px] text-zinc-500">
                        ✓ Handtekening ingesteld ({m.signature_html.length} tekens)
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {!m.is_default && (
                    <button
                      type="button"
                      data-testid={`set-default-btn-${m.mailbox_id}`}
                      onClick={() => setDefault(m.mailbox_id)}
                      title="Maak standaard"
                      className="px-2 py-1.5 text-xs border border-zinc-200 text-zinc-600 rounded-sm hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700 transition-colors"
                    >
                      <StarHalf size={12} />
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`edit-btn-${m.mailbox_id}`}
                    onClick={() => startEdit(m)}
                    title="Bewerken"
                    className="px-2 py-1.5 text-xs border border-zinc-200 text-zinc-600 rounded-sm hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-colors"
                  >
                    <PencilSimple size={12} />
                  </button>
                  <button
                    type="button"
                    data-testid={`delete-btn-${m.mailbox_id}`}
                    onClick={() => setConfirmDelete(m)}
                    title="Verwijderen"
                    disabled={m.is_default}
                    className="px-2 py-1.5 text-xs border border-zinc-200 text-zinc-600 rounded-sm hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm delete */}
      {confirmDelete && (
        <div
          className="fixed inset-0 bg-zinc-900/40 flex items-center justify-center p-4 z-50"
          data-testid="delete-confirm-overlay"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-white rounded-sm border border-zinc-200 shadow-lg p-5 max-w-sm w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-2">
              <Warning size={20} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-heading text-base font-semibold text-[#0F172A]">
                  Verwijder mailbox?
                </h4>
                <p className="text-xs text-zinc-600 mt-1">
                  <strong>{confirmDelete.email}</strong> wordt gedeactiveerd.
                  Bestaande campagnes blijven werken; alleen niet meer beschikbaar voor nieuwe campagnes.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                data-testid="delete-cancel-btn"
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-3 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors"
              >
                Annuleer
              </button>
              <button
                type="button"
                data-testid="delete-confirm-btn"
                onClick={() => remove(confirmDelete.mailbox_id)}
                className="flex-1 px-3 py-2 text-xs bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors flex items-center justify-center gap-1"
              >
                <X size={12} /> Verwijder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ImapSmtpFields({ form, setForm, editing, onTest, testing, testResult }) {
  const updateImap = (k, v) => setForm(f => ({ ...f, imap: { ...f.imap, [k]: v } }));
  const updateSmtp = (k, v) => setForm(f => ({ ...f, smtp: { ...f.smtp, [k]: v } }));

  return (
    <div className="mb-4 border border-indigo-100 bg-indigo-50/30 rounded-sm p-4" data-testid="imap-smtp-fields">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Plug size={14} weight="bold" className="text-indigo-600" />
          <h4 className="text-xs uppercase tracking-wider font-semibold text-indigo-700">IMAP/SMTP instellingen</h4>
        </div>
        <button
          type="button"
          data-testid="test-connection-btn"
          onClick={onTest}
          disabled={!editing || testing}
          title={!editing ? "Eerst opslaan om verbinding te testen" : "Test IMAP + SMTP login"}
          className="px-3 py-1.5 text-[11px] font-medium bg-indigo-600 text-white rounded-sm hover:bg-indigo-700 transition-colors disabled:opacity-40 flex items-center gap-1"
        >
          <Plug size={11} /> {testing ? 'Testen...' : 'Test verbinding'}
        </button>
      </div>

      {testResult && (
        <div
          data-testid="test-result"
          className={`mb-3 px-3 py-2 text-xs rounded-sm border ${testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}
        >
          {testResult.ok ? (
            <>✓ Verbinding gelukt — IMAP & SMTP login succesvol.</>
          ) : (
            <div className="space-y-1">
              <div>✕ Verbinding mislukt.</div>
              {testResult.imap && <div><strong>IMAP:</strong> {testResult.imap.ok ? '✓ OK' : `✕ ${testResult.imap.error}`}</div>}
              {testResult.smtp && <div><strong>SMTP:</strong> {testResult.smtp.ok ? '✓ OK' : `✕ ${testResult.smtp.error}`}</div>}
              {testResult.error && <div>{testResult.error}</div>}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* IMAP */}
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1">
            <Envelope size={11} /> Inkomend (IMAP)
          </div>
          <Field label="Server" testid="imap-host" placeholder="imap.transip.email"
                 value={form.imap.host} onChange={v => updateImap('host', v)} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Poort" testid="imap-port" type="number" placeholder="993"
                   value={form.imap.port} onChange={v => updateImap('port', parseInt(v) || 0)} />
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Beveiliging</label>
              <select
                data-testid="imap-ssl"
                value={form.imap.ssl ? 'ssl' : 'none'}
                onChange={e => updateImap('ssl', e.target.value === 'ssl')}
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
              >
                <option value="ssl">SSL/TLS</option>
                <option value="none">Geen</option>
              </select>
            </div>
          </div>
          <Field label="Gebruikersnaam" testid="imap-username" placeholder="info@horseappcompare.com"
                 value={form.imap.username} onChange={v => updateImap('username', v)} />
          <Field label={editing ? "Wachtwoord (laat leeg om te behouden)" : "Wachtwoord *"}
                 testid="imap-password" type="password"
                 icon={<Lock size={10} />}
                 value={form.imap.password} onChange={v => updateImap('password', v)} />
          <Field label="Inbox map" testid="imap-folder" placeholder="INBOX"
                 value={form.imap.inbox_folder} onChange={v => updateImap('inbox_folder', v)} />
        </div>

        {/* SMTP */}
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1">
            <Envelope size={11} /> Uitgaand (SMTP)
          </div>
          <Field label="Server" testid="smtp-host" placeholder="smtp.transip.email"
                 value={form.smtp.host} onChange={v => updateSmtp('host', v)} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Poort" testid="smtp-port" type="number" placeholder="465"
                   value={form.smtp.port} onChange={v => updateSmtp('port', parseInt(v) || 0)} />
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Beveiliging</label>
              <select
                data-testid="smtp-ssl-mode"
                value={form.smtp.ssl_mode}
                onChange={e => updateSmtp('ssl_mode', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
              >
                <option value="ssl">SSL/TLS (465)</option>
                <option value="starttls">STARTTLS (587)</option>
                <option value="none">Geen</option>
              </select>
            </div>
          </div>
          <Field label="Gebruikersnaam" testid="smtp-username" placeholder="info@horseappcompare.com"
                 value={form.smtp.username} onChange={v => updateSmtp('username', v)} />
          <Field label={editing ? "Wachtwoord (laat leeg om te behouden)" : "Wachtwoord *"}
                 testid="smtp-password" type="password"
                 icon={<Lock size={10} />}
                 value={form.smtp.password} onChange={v => updateSmtp('password', v)} />
        </div>
      </div>

      <p className="mt-3 text-[11px] text-zinc-500">
        Wachtwoorden worden versleuteld opgeslagen (AES-256 via Fernet) en zijn nooit zichtbaar in de UI.
        Voor TransIP: imap.transip.email:993 (SSL) en smtp.transip.email:465 (SSL).
      </p>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder, type = 'text', icon }) {
  return (
    <div>
      <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">{icon}{label}</label>
      <input
        data-testid={`form-${testid}`}
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={type === 'password' ? 'new-password' : 'off'}
        className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
      />
    </div>
  );
}
